import {ServiceClass} from '@/decorators';
import {AnyObject} from '@/globals';
import {
    TerminalBlockDataModel,
    TerminalBlockDataValueDto,
    TerminalBlockModel,
    TerminalBlockSaveDto,
    TerminalBlockStructuredResponse,
    TerminalBlockTableCellDto,
} from '@/sdk';
import {BaseApi} from './base.api';

@ServiceClass()
export class TerminalBlockApi extends BaseApi {
    // Terminal Block Management

    /**
     * Get all terminal blocks
     */
    public async getAll(): Promise<TerminalBlockModel[]> {
        return this.GET_RequestAsync<TerminalBlockModel[]>(`${this.ApiUrl}/terminal-blocks`);
    }

    /**
     * Get all terminal blocks by location ID
     */
    public async getByLocationId(locationId: string) {
        return this.GET_RequestAsync<TerminalBlockModel>(`${this.ApiUrl}/terminal-blocks/by-location/${locationId}`);
    }

    /**
     * Get all terminal blocks for a specific junction box
     */
    public async getByJunctionBoxId(junctionBoxId: string) {
        return this.GET_RequestAsync<TerminalBlockModel>(`${this.ApiUrl}/terminal-blocks/by-junction-box/${junctionBoxId}`);
    }

    /**
     * Get a specific terminal block by ID with full structured data
     */
    public async getById(id: string): Promise<TerminalBlockStructuredResponse> {
        return this.GET_RequestAsync<TerminalBlockStructuredResponse>(`${this.ApiUrl}/terminal-blocks/${id}`);
    }

    /**
     * Save terminal block (create or update based on presence of ID)
     */
    public async save(terminalBlock: TerminalBlockSaveDto): Promise<TerminalBlockModel> {
        return this.POST_RequestAsync<TerminalBlockModel>(`${this.ApiUrl}/terminal-blocks/save`, terminalBlock);
    }

    /**
     * Delete a terminal block
     */
    public async delete(id: string): Promise<void> {
        return this.DELETE_RequestAsync<void>(`${this.ApiUrl}/terminal-blocks/${id}`);
    }

    // Data Management

    /**
     * Add a current value to a terminal block
     */
    public async addCurrent(terminalBlockId: string, data: TerminalBlockDataValueDto): Promise<TerminalBlockDataModel> {
        return this.POST_RequestAsync<TerminalBlockDataModel>(`${this.ApiUrl}/terminal-blocks/${terminalBlockId}/currents`, data);
    }

    /**
     * Add a cross-section value to a terminal block
     */
    public async addCrossSection(terminalBlockId: string, data: TerminalBlockDataValueDto): Promise<TerminalBlockDataModel> {
        return this.POST_RequestAsync<TerminalBlockDataModel>(`${this.ApiUrl}/terminal-blocks/${terminalBlockId}/cross-sections`, data);
    }

    /**
     * Add an intersection value to the matrix
     */
    public async addIntersectionValue(terminalBlockId: string, data: TerminalBlockTableCellDto[]): Promise<TerminalBlockDataModel[]> {
        return this.POST_RequestAsync<TerminalBlockDataModel[]>(`${this.ApiUrl}/terminal-blocks/${terminalBlockId}/intersection-values`, data);
    }

    /**
     * Update a table cell value
     */
    // public async updateTableCell(terminalBlockId: string, data: TerminalBlockTableCellDto): Promise<TerminalBlockDataModel> {
    //     return this.PATCH_RequestAsync<TerminalBlockDataModel>(`${this.ApiUrl}/terminal-blocks/${terminalBlockId}/table-cell`, data);
    // }

    /**
     * Update a data entry value
     */
    // public async updateDataValue(dataId: string, data: TerminalBlockDataValueDto): Promise<void> {
    //     return this.PATCH_RequestAsync<void>(`${this.ApiUrl}/terminal-blocks/data/${dataId}`, data);
    // }

    /**
     * Delete a data entry
     */
    public async deleteData(dataId: string): Promise<void> {
        return this.DELETE_RequestAsync<void>(`${this.ApiUrl}/terminal-blocks/data/${dataId}`);
    }

    /**
     * Get structured data for a terminal block
     */
    // public async getStructuredData(terminalBlockId: string): Promise<TerminalBlockMatrix> {
    //     return this.GET_RequestAsync<TerminalBlockMatrix>(`${this.ApiUrl}/terminal-blocks/${terminalBlockId}/structured-data`);
    // }

    // Legacy methods for backward compatibility

    /**
     * @deprecated Use save() instead
     */
    public create(terminalBlock: TerminalBlockModel) {
        return this.POST_Request<TerminalBlockModel>(`${this.ApiUrl}/terminal-blocks`, terminalBlock);
    }

    /**
     * @deprecated Use save() instead
     */
    public update(id: string, terminalBlock: TerminalBlockModel) {
        return this.PATCH_Request<TerminalBlockModel>(`${this.ApiUrl}/terminal-blocks/${id}`, terminalBlock);
    }

    /**
     * Search terminal blocks with filters (if needed for legacy support)
     */
    public search(filters: AnyObject = {}) {
        const filterQuery = encodeURIComponent(JSON.stringify(filters));
        return this.GET_Request<Array<TerminalBlockModel>>(`${this.ApiUrl}/terminal-blocks/search?filter=${filterQuery}`);
    }
}
